import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NbButtonModule, NbCardModule, NbInputModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  CleaningTimeEstimate,
  CleaningTimeEstimateArea,
  CleaningTimeEstimateAreaDisplay
} from 'src/app/models/qa-models/cleaning-time-estimate';
import { Robot } from 'src/app/models/robot';
import {
  CLEANING_PARAMETERS,
  SUCTION_LEVEL_LABELS,
  COMBINED_SUCTION_LABELS,
  CLEANING_MODE_LABELS,
  ENABLED_DISABLED_LABELS,
  PAD_PLATE_LEVEL_LABELS
} from 'src/app/shared/constants/general-constants';
import { SharedModule } from 'src/app/shared/shared.module';
import { LoadAllRobots, LoadOwnerRobots } from '../redux';
import { QaState } from '../redux/qa.state';
import { CleaningTimeEstimateService } from '../services/cleaning-time-estimate.service';
import { QaServiceService } from '../services/qa-service.service';

@Component({
  selector: 'app-clean-time-estimates',
  templateUrl: './clean-time-estimates.component.html',
  styleUrls: ['./clean-time-estimates.component.scss'],
  standalone: true,
  imports: [SharedModule, NbCardModule, NbButtonModule, NbInputModule, SharedModule]
})
export class CleanTimeEstimatesComponent implements OnInit, OnDestroy {
  public robot$: Observable<any[]> = inject(Store).select(QaState.robots);
  public ownerRobot$: Observable<any[]> = inject(Store).select(QaState.ownerRobots);
  private unsubscribe = new Subject<void>();

  public myRobots: Robot[] = [];
  public commonRobots: Robot[] = [];
  public cleaningTimeEstimates: CleaningTimeEstimate[] = [];
  public cleaningTimeEstimateForm: UntypedFormGroup;
  public selectedRobot: Robot = null;
  public submitting = false;
  public spin = false;
  public cleaningParameters = CLEANING_PARAMETERS;

  constructor(
    private store: Store,
    public service: QaServiceService,
    private cleaningTimeEstimateService: CleaningTimeEstimateService
  ) {
    if (this.store.selectSnapshot(QaState.robots).length < 1) {
      this.store.dispatch(new LoadAllRobots());
    }
    if (!this.store.selectSnapshot(QaState.ownerRobots)) {
      this.store.dispatch(new LoadOwnerRobots());
    }
  }

  public ngOnInit(): void {
    this.robot$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.commonRobots = data));
    this.ownerRobot$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.myRobots = data));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public groupByFn = (item: any) => {
    return this.myRobots?.some((s: any) => s.id === item.id) ? 'My Robots' : 'Common Robots';
  };

  private getParamKeysForArea(area: CleaningTimeEstimateArea): string[] {
    const keySet = new Set<string>();
    for (const estimate of area.estimates) {
      const params = estimate?.area_params;
      if (params && typeof params === 'object') {
        Object.keys(params).forEach((k) => keySet.add(k));
      }
    }
    let keys = [...keySet].filter((key) => key !== 'noAutoPasses').sort();
    if (keys.includes('carpetBoost') && keys.includes('vacHigh')) {
      keys = keys
        .filter((k) => k !== 'carpetBoost' && k !== 'vacHigh')
        .concat('suction')
        .sort();
    }
    return keys;
  }

  private getParamLabel(key: string): string {
    return this.cleaningParameters.find((p) => p.value === key)?.name ?? key;
  }

  private buildAreaTableDisplay(area: CleaningTimeEstimateArea): CleaningTimeEstimateAreaDisplay {
    const paramKeys = this.getParamKeysForArea(area);
    if (!paramKeys.length) return undefined;
    const columns = paramKeys.map((key) => ({ key, label: this.getParamLabel(key) }));
    const rows = area.estimates.map((est) => ({
      paramDisplayByKey: Object.fromEntries(
        paramKeys.map((key) => [key, this.formatParamDisplayValue(key, est.area_params?.[key], est.area_params)])
      ),
      valueDisplay: this.formatDurationSeconds(est.value)
    }));
    return { columns, rows };
  }

  private formatParamDisplayValue(key: string, value: any, params?: Record<string, unknown>): string {
    if (value == null || value === '') return 'N/A';
    if (key === 'suction') {
      return COMBINED_SUCTION_LABELS[`${params['carpetBoost']},${params['vacHigh']}`] ?? 'N/A';
    }
    if (key === 'padWetness') {
      return this.formatPadWetness(value);
    }
    if (['reusable', 'disposable', 'padPlate'].includes(key)) {
      return value in PAD_PLATE_LEVEL_LABELS ? PAD_PLATE_LEVEL_LABELS[value] : String(value);
    }
    if (key === 'swScrub' || key === 'gentleMode') {
      return ENABLED_DISABLED_LABELS[value];
    }
    if (key === 'operatingMode') {
      return CLEANING_MODE_LABELS[value];
    }
    if (['suctionLevel', 'carpetBoost', 'vacHigh', 'suction'].includes(key)) {
      return SUCTION_LEVEL_LABELS[value];
    }
    if (['twoPass', 'noAutoPasses', 'cleaningPasses'].includes(key)) {
      const isTrue = value === '1' || value === 'true' || value === true;
      return isTrue ? '2' : '1';
    }
    return JSON.stringify(value);
  }

  private formatPadWetness(value: unknown): string {
    return PAD_PLATE_LEVEL_LABELS[Object.values(value)[0]];
  }

  private formatDurationSeconds(value: number | null | undefined): string {
    const totalSeconds = Math.round(Number(value));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  public syncValueDisplay(area: CleaningTimeEstimateArea, index: number): void {
    const row = area.display?.rows[index];
    const estimate = area.estimates[index];
    row.valueDisplay = this.formatDurationSeconds(estimate.value);
  }

  public robotIdAdd(event: Robot) {
    this.cleaningTimeEstimates = [];
    this.selectedRobot = event;
    this.spin = this.selectedRobot !== null;
    if (event?.id) {
      this.cleaningTimeEstimateService.loadCleaningTimeEstimate(event.id).then((data) => {
        this.spin = false;
        if (data) {
          const raw = this.cleaningTimeEstimates.concat(data);
          this.cleaningTimeEstimates = raw.map((estimate) => ({
            ...estimate,
            cleaning_time_estimates: [...(estimate.cleaning_time_estimates)]
              .sort((a, b) => (a.area_id).localeCompare(b.area_id))
              .map((area) => ({
                ...area,
                display: this.buildAreaTableDisplay(area)
              }))
          }));
        }
      });
    }
  }

  public robotIdClear() {
    this.cleaningTimeEstimates = [];
    this.selectedRobot = null;
    this.spin = false;
  }

  public onSubmit(): void {
    if (!this.selectedRobot?.id || !this.cleaningTimeEstimates?.length) return;
    this.submitting = true;
    this.cleaningTimeEstimateService
      .updateCleaningTimeEstimates(this.selectedRobot.id, this.cleaningTimeEstimates)
      .then((ok) => {
        if (ok) {
          this.robotIdAdd(this.selectedRobot);
        }
      })
      .finally(() => (this.submitting = false));
  }
}
